import type { Controls } from '../core/device';
import { clamp } from '../core/math';
import type { Random } from '../core/Random';
import { banner } from './draw';
import type { MiniGame, MiniInput } from './types';

/** Dartboard numbers, clockwise from the top. */
export const DART_ORDER = [20, 1, 18, 4, 13, 6, 10, 15, 2, 17, 3, 19, 7, 16, 8, 11, 14, 9, 12, 5];
const ORDER = DART_ORDER;
/** Ring radii as fractions of the board (regulation-ish). */
export const DART_RINGS = {
  bull: 0.037,
  outerBull: 0.094,
  tripleIn: 0.582,
  tripleOut: 0.629,
  doubleIn: 0.953,
  doubleOut: 1,
};
const R = DART_RINGS;

/** Points for a dart landing at (x, y) relative to the center, in board radii (y up is negative). */
export function dartScore(x: number, y: number): number {
  const r = Math.hypot(x, y);
  if (r > R.doubleOut) return 0;
  if (r <= R.bull) return 50;
  if (r <= R.outerBull) return 25;
  // Angle clockwise from straight up.
  const a = (Math.atan2(x, -y) + Math.PI * 2) % (Math.PI * 2);
  const seg = ORDER[Math.floor(((a + Math.PI / 20) % (Math.PI * 2)) / (Math.PI / 10))] as number;
  if (r >= R.tripleIn && r <= R.tripleOut) return seg * 3;
  if (r >= R.doubleIn) return seg * 2;
  return seg;
}

const ROUNDS = 3;
const DARTS = 3;

interface Dart {
  x: number;
  y: number;
  who: 'you' | 'them';
  points: number;
}

/**
 * Bar darts against one of the regulars: three rounds of three darts, highest total
 * wins. Your aim drifts (more when you've been drinking); throw when it's where you
 * want it.
 */
export class DartsGame implements MiniGame {
  readonly title: string;
  readonly width = 360;
  readonly height = 240;
  score = { you: 0, them: 0 };
  darts: Dart[] = [];
  round = 1;
  thrown = 0;
  phase: 'you' | 'them' | 'over' = 'you';
  /** Every dart thrown this game, in order (the 3D board animates new ones in). */
  readonly history: Dart[] = [];
  private t = 0;
  private overT = -1;
  private wait = 0;
  private aim = { x: 0, y: 0 };
  private readonly wobble: number;

  constructor(
    private readonly rng: Random,
    readonly opponent: string,
    /** 0 = can barely see the board, 1 = league night champion. */
    private readonly skill: number,
    buzz = 0,
  ) {
    this.title = `Darts vs. ${opponent}`;
    this.wobble = 0.22 + clamp(buzz / 100, 0, 1) * 0.35;
  }

  get done(): boolean {
    return this.phase === 'over' && this.t - this.overT > 1.4;
  }

  get won(): boolean {
    return this.score.you > this.score.them;
  }

  hint(controls: Controls): string {
    return `${{ keys: 'Press Space/E', touch: 'Tap', pad: 'Press A' }[controls]} to throw when the crosshair's where you want it. Bull is 50, the thin outer ring doubles, the inner ring triples.`;
  }

  /** Where your aim is right now (board radii). */
  aimAt(): { x: number; y: number } {
    return { ...this.aim };
  }

  update(dt: number, input: MiniInput): void {
    this.t += dt;
    if (this.phase === 'over') return;
    // A slow figure-eight drift, plus a shake.
    const w = this.wobble;
    this.aim.x = Math.sin(this.t * 1.7) * w + Math.sin(this.t * 4.3) * w * 0.25;
    this.aim.y = Math.sin(this.t * 2.3 + 1) * w * 0.8 - 0.6 + Math.cos(this.t * 5.1) * w * 0.2;
    if (this.phase === 'you') {
      if (input.pressed || input.tap) this.throwDart('you', this.aim.x, this.aim.y);
      return;
    }
    // Their turn: aim for triple 20 and hope.
    this.wait -= dt;
    if (this.wait > 0) return;
    this.wait = 0.55;
    const spread = 0.08 + (1 - this.skill) * 0.3;
    this.throwDart('them', this.rng.range(-1, 1) * spread, -0.6 + this.rng.range(-1, 1) * spread);
  }

  private throwDart(who: 'you' | 'them', x: number, y: number): void {
    // Release is never perfect.
    const jx = x + this.rng.range(-0.03, 0.03);
    const jy = y + this.rng.range(-0.03, 0.03);
    const points = dartScore(jx, jy);
    const dart = { x: jx, y: jy, who, points };
    this.darts.push(dart);
    this.history.push(dart);
    this.score[who] += points;
    this.thrown++;
    if (this.thrown < DARTS) return;
    this.thrown = 0;
    if (who === 'you') {
      this.phase = 'them';
      this.wait = 0.8;
    } else if (this.round >= ROUNDS) {
      this.phase = 'over';
      this.overT = this.t;
    } else {
      this.round++;
      this.phase = 'you';
      this.darts = [];
    }
  }

  draw(g: CanvasRenderingContext2D, time: number): void {
    const W = this.width;
    const H = this.height;
    g.fillStyle = '#2b1d14';
    g.fillRect(0, 0, W, H);
    // Cork backing and the board.
    const cx = 150;
    const cy = 122;
    const rad = 100;
    g.fillStyle = '#7a5a3a';
    g.beginPath();
    g.arc(cx, cy, rad + 12, 0, Math.PI * 2);
    g.fill();
    for (let i = 0; i < 20; i++) {
      const a0 = -Math.PI / 2 - Math.PI / 20 + (i * Math.PI) / 10;
      const a1 = a0 + Math.PI / 10;
      const dark = i % 2 === 0;
      const ring = (r0: number, r1: number, color: string): void => {
        g.fillStyle = color;
        g.beginPath();
        g.arc(cx, cy, r1 * rad, a0, a1);
        g.arc(cx, cy, r0 * rad, a1, a0, true);
        g.closePath();
        g.fill();
      };
      ring(R.outerBull, R.doubleOut, dark ? '#1b1b1b' : '#efe3c6');
      ring(R.tripleIn, R.tripleOut, dark ? '#c62828' : '#2f7d32');
      ring(R.doubleIn, R.doubleOut, dark ? '#c62828' : '#2f7d32');
      g.fillStyle = '#f2efe6';
      g.font = '11px Anton, Impact, sans-serif';
      g.textAlign = 'center';
      const am = a0 + Math.PI / 20;
      g.fillText(String(ORDER[i]), cx + Math.cos(am) * (rad + 6), cy + Math.sin(am) * (rad + 6) + 4);
    }
    g.fillStyle = '#2f7d32';
    g.beginPath();
    g.arc(cx, cy, R.outerBull * rad, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = '#c62828';
    g.beginPath();
    g.arc(cx, cy, R.bull * rad, 0, Math.PI * 2);
    g.fill();
    // Darts in the board.
    for (const d of this.darts) {
      g.fillStyle = d.who === 'you' ? '#ffd400' : '#5ff3ff';
      g.beginPath();
      g.arc(cx + d.x * rad, cy + d.y * rad, 3.5, 0, Math.PI * 2);
      g.fill();
    }
    // Crosshair.
    if (this.phase === 'you') {
      const ax = cx + this.aim.x * rad;
      const ay = cy + this.aim.y * rad;
      g.strokeStyle = '#ffffff';
      g.lineWidth = 2;
      g.beginPath();
      g.arc(ax, ay, 7, 0, Math.PI * 2);
      g.moveTo(ax - 12, ay);
      g.lineTo(ax + 12, ay);
      g.moveTo(ax, ay - 12);
      g.lineTo(ax, ay + 12);
      g.stroke();
    }
    // Scoreboard chalk.
    g.fillStyle = '#1e2b22';
    g.fillRect(268, 14, 84, 120);
    g.fillStyle = '#f2efe6';
    g.font = '14px "Permanent Marker", cursive';
    g.textAlign = 'center';
    g.fillText(`ROUND ${this.round}/${ROUNDS}`, 310, 34);
    g.fillStyle = '#ffd400';
    g.fillText(`YOU ${this.score.you}`, 310, 64);
    g.fillStyle = '#5ff3ff';
    g.fillText(`${this.opponent.split(' ')[0]?.toUpperCase()} ${this.score.them}`, 310, 92);
    g.fillStyle = '#f2efe6';
    g.font = '11px "Permanent Marker", cursive';
    g.fillText(
      this.phase === 'you'
        ? `YOUR THROW ${this.thrown + 1}/${DARTS}`
        : this.phase === 'them'
          ? 'THEIR THROW'
          : '',
      310,
      120,
    );
    if (this.phase === 'over')
      banner(
        g,
        this.won ? 'YOU WIN!' : this.score.you === this.score.them ? 'TIE!' : 'YOU LOSE',
        150,
        130,
        this.won ? '#9dff6a' : '#ff8a8a',
        38,
      );
    void time;
  }
}
